import { Model } from '@/clients/http'

import { DeliveryMethodHttpParams, DeliveryMethodResponses } from '@/types'

export class DeliveryMethodModel extends Model {
  // Define correlation keys for Redux state management
  public static readonly correlations = {
    GET_LIST: 'GET_DELIVERY_METHOD_LIST',
    GET_BY_KEY: 'GET_DELIVERY_METHOD_BY_KEY',
    CREATE: 'CREATE_DELIVERY_METHOD',
    UPDATE: 'UPDATE_DELIVERY_METHOD',
    DELETE: 'DELETE_DELIVERY_METHOD',
  }

  // Define endpoint builders
  public static readonly endpoints = {
    getList: (props?: DeliveryMethodHttpParams.GetList) =>
      this.createEndpoint({
        url: '/api/backoffice/delivery-method',
        params: props?.params,
        query: props?.query,
      }),

    getByKey: (props: DeliveryMethodHttpParams.GetByKey) =>
      this.createEndpoint({
        url: '/api/backoffice/delivery-method/:key',
        params: props?.params,
        query: props?.query,
      }),

    create: (props: DeliveryMethodHttpParams.Create) =>
      this.createEndpoint({
        url: '/api/backoffice/delivery-method',
        params: props?.params,
        query: props?.query,
      }),

    update: (props: DeliveryMethodHttpParams.Update) =>
      this.createEndpoint({
        url: '/api/backoffice/delivery-method/:key',
        params: props?.params,
        query: props?.query,
      }),

    delete: (props: DeliveryMethodHttpParams.Delete) =>
      this.createEndpoint({
        url: '/api/backoffice/delivery-method/:id',
        params: props?.params,
        query: props?.query,
      }),
  }

  /**
   * Get paginated list of delivery methods
   */
  public static readonly getList = async (props?: DeliveryMethodHttpParams.GetList) => {
    return this.createHttpRequest<
      DeliveryMethodResponses.GetList,
      DeliveryMethodResponses.GetListError
    >({
      correlationKey: this.correlations.GET_LIST,
      payload: {
        method: 'get',
        url: this.endpoints.getList(props),
        useBearerToken: true,
      },
      store: props?.store,
    })
  }

  /**
   * Get a single delivery method by Key
   */
  public static readonly getByKey = async (props: DeliveryMethodHttpParams.GetByKey) => {
    return this.createHttpRequest<
      DeliveryMethodResponses.GetByKey,
      DeliveryMethodResponses.GetByKeyError
    >({
      correlationKey: this.correlations.GET_BY_KEY,
      payload: {
        method: 'get',
        url: this.endpoints.getByKey(props),
        useBearerToken: true,
      },
      store: props?.store,
    })
  }

  /**
   * Create a new delivery method
   */
  public static readonly create = async (props: DeliveryMethodHttpParams.Create) => {
    return this.createHttpRequest<
      DeliveryMethodResponses.Create,
      DeliveryMethodResponses.CreateError
    >({
      correlationKey: this.correlations.CREATE,
      payload: {
        method: 'post',
        url: this.endpoints.create(props),
        body: props.body,
        useBearerToken: true,
      },
      store: props?.store,
    })
  }

  /**
   * Update an existing delivery method
   */
  public static readonly update = async (props: DeliveryMethodHttpParams.Update) => {
    return this.createHttpRequest<
      DeliveryMethodResponses.Update,
      DeliveryMethodResponses.UpdateError
    >({
      correlationKey: this.correlations.UPDATE,
      payload: {
        method: 'put',
        url: this.endpoints.update(props),
        body: props.body,
        useBearerToken: true,
      },
      store: props?.store,
    })
  }

  /**
   * Delete a delivery method
   */
  public static readonly delete = async (props: DeliveryMethodHttpParams.Delete) => {
    return this.createHttpRequest<
      DeliveryMethodResponses.Delete,
      DeliveryMethodResponses.DeleteError
    >({
      correlationKey: this.correlations.DELETE,
      payload: {
        method: 'delete',
        url: this.endpoints.delete(props),
        useBearerToken: true,
      },
      store: props?.store,
    })
  }
}
